import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Mail, RefreshCcw, Sparkles, Send, Type, Code2 } from "lucide-react";
import { toast } from "sonner";

export default function WishlistWeeklyPreview() {
  const nav = useNavigate();
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [lastSentTo, setLastSentTo] = useState("");
  const [mode, setMode] = useState("html"); // "html" | "plain"

  const load = () => {
    setLoading(true);
    api
      .get("/wishlist/weekly-preview")
      .then((r) => setPreview(r.data))
      .catch((err) => toast.error(apiError(err)))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const sendTest = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/wishlist/weekly-preview/send-test");
      setLastSentTo(data.sent_to);
      toast.success(`Sent to ${data.sent_to}`);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <Layout
      title="Weekly digest preview"
      subtitle="The exact email guests on `alert_frequency=weekly` will receive on the next Monday cron."
      actions={
        <>
          <Button
            variant="outline"
            className="rounded-full h-11 px-5"
            onClick={() => nav(-1)}
            data-testid="digest-preview-back"
          >
            <ArrowLeft className="w-4 h-4 mr-1.5" /> Back
          </Button>
          <Button
            variant="outline"
            className="rounded-full h-11 px-5"
            onClick={sendTest}
            disabled={sending || !preview}
            data-testid="digest-preview-send-test"
          >
            <Send className={`w-4 h-4 mr-1.5 ${sending ? "animate-pulse" : ""}`} />
            {sending ? "Sending…" : "Send to my inbox"}
          </Button>
          <Button
            className="rounded-full h-11 px-5 ns-btn-primary"
            onClick={load}
            disabled={loading}
            data-testid="digest-preview-refresh"
          >
            <RefreshCcw className={`w-4 h-4 mr-1.5 ${loading ? "animate-spin" : ""}`} />
            {loading ? "Rebuilding…" : "Refresh"}
          </Button>
        </>
      }
    >
      {!preview ? (
        <div className="ns-card p-10 text-center text-slate-400 italic text-sm">
          {loading ? "Building preview…" : "No preview loaded."}
        </div>
      ) : (
        <div className="space-y-4" data-testid="digest-preview-root">
          <div className="ns-card p-4 flex items-center gap-3 text-sm">
            <Mail className="w-4 h-4 text-teal-700" />
            <div className="flex-1 min-w-0 truncate">
              <span className="ns-label text-slate-400 mr-2">Subject</span>
              <span className="font-medium">{preview.subject}</span>
            </div>
            <div className="text-xs text-slate-500">
              {preview.hit_count} window{preview.hit_count === 1 ? "" : "s"}
            </div>
            {preview.synthetic && (
              <span
                data-testid="digest-preview-synthetic"
                className="inline-flex items-center gap-1 rounded-full bg-amber-100 text-amber-800 text-[10px] font-medium px-2.5 py-0.5"
              >
                <Sparkles className="w-3 h-3" /> Synthetic
              </span>
            )}
          </div>
          {lastSentTo && (
            <div
              data-testid="digest-preview-sent-confirmation"
              className="ns-card p-3 flex items-center gap-2 bg-emerald-50/60 border border-emerald-100 text-xs text-emerald-800"
            >
              <Send className="w-3.5 h-3.5" />
              Test email sent to <strong className="ml-1">{lastSentTo}</strong> — check your inbox.
            </div>
          )}
          <div className="ns-card p-4">
            <div className="flex items-center justify-end mb-3">
              <div className="flex rounded-full bg-stone-100 p-1 text-xs" data-testid="digest-preview-mode-toggle">
                <button
                  data-testid="digest-preview-mode-html"
                  onClick={() => setMode("html")}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full transition ${
                    mode === "html"
                      ? "bg-white text-teal-800 shadow-sm font-medium"
                      : "text-slate-500 hover:text-slate-700"
                  }`}
                >
                  <Code2 className="w-3 h-3" /> Email look
                </button>
                <button
                  data-testid="digest-preview-mode-plain"
                  onClick={() => setMode("plain")}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full transition ${
                    mode === "plain"
                      ? "bg-white text-teal-800 shadow-sm font-medium"
                      : "text-slate-500 hover:text-slate-700"
                  }`}
                >
                  <Type className="w-3 h-3" /> Plain text
                </button>
              </div>
            </div>
            {mode === "html" ? (
              <iframe
                title="Weekly digest preview"
                data-testid="digest-preview-iframe"
                srcDoc={`<html><head><style>body{margin:24px;background:#f8fafc}</style></head><body>${preview.html}</body></html>`}
                className="w-full h-[640px] bg-white rounded-lg border border-slate-200"
              />
            ) : (
              <pre
                data-testid="digest-preview-plain"
                className="w-full h-[640px] overflow-auto bg-white rounded-lg border border-slate-200 p-6 text-sm font-mono text-slate-700 whitespace-pre-wrap leading-relaxed"
              >
                {preview.plain || preview.html.replace(/<[^>]+>/g, "")}
              </pre>
            )}
          </div>
        </div>
      )}
    </Layout>
  );
}
